import type { ResultDetail } from '../../shared/api';
import { QueryHttpError } from '../api/http';
import { readArchivedResult } from '../api/results';

const RETRY_DELAY_MS = 2_000;

export interface ArchiveContext {
  readonly identity: number;
  readonly credential: string | null;
}
interface ArchiveHooks {
  isCurrent(context: ArchiveContext): boolean;
  pending(roundId: string): void;
  success(result: ResultDetail): void;
  terminal(status: number, credential: string | null): void;
  retrying(): void;
}

/** Owns request/timer cancellation, not session state or socket lifetime. */
export function createArchiveRetry(hooks: ArchiveHooks) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let revision = 0;
  let loading = false;
  function cancel(): void {
    revision++;
    loading = false;
    clearTimeout(timer);
  }
  async function load(roundId: string, context: ArchiveContext): Promise<void> {
    if (loading || !hooks.isCurrent(context)) return;
    loading = true;
    hooks.pending(roundId);
    clearTimeout(timer);
    const request = ++revision;
    const current = () => request === revision && hooks.isCurrent(context);
    try {
      const result = await readArchivedResult(roundId, context.credential ?? '');
      if (!current() || result.roundId !== roundId) return;
      hooks.success(result);
    } catch (error) {
      if (!current()) return;
      // 4xx is a definite answer for this credential; 5xx and network failures retry.
      if (error instanceof QueryHttpError && error.status < 500) {
        hooks.terminal(error.status, context.credential);
        return;
      }
      timer = setTimeout(() => void load(roundId, context), RETRY_DELAY_MS);
      hooks.retrying();
    } finally {
      if (request === revision) loading = false;
    }
  }
  return { load, cancel, dispose: cancel };
}
